import { useState, useEffect } from "react";
import {
  Flex,
  Heading,
  Text,
  Box,
  NumberInput,
  NumberInputField,
  Button,
  Alert,
  AlertIcon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CardRevealSlot } from "../../components";
import { useAppContext } from "../../context/appContext/appContext";
import {
  drawRandomCards,
  calculateGameCount,
} from "../../utils/practiceCounting";
import { GAME_HISTORY_NAMES } from "../../utils/gameHistoryNames";

const GAME_NAME = GAME_HISTORY_NAMES.COUNTING;
const CARD_COUNT = 24;
const MIN_DELAY_MS = 300;
const MAX_DELAY_MS = 2000;
const DEFAULT_DELAY_MS = 1000;
const SPEED_STEP_MS = 100;

const PracticeCountingGame = () => {
  const navigate = useNavigate();
  const { user } = useAppContext();

  const [cards, setCards] = useState(() => drawRandomCards(CARD_COUNT));
  const [flipIndex, setFlipIndex] = useState(0);
  const [phase, setPhase] = useState("ready"); // "ready" | "revealing" | "guessing" | "result"
  const [guess, setGuess] = useState("");
  const [result, setResult] = useState(null);
  const [transitionDelayMs, setTransitionDelayMs] = useState(DEFAULT_DELAY_MS);

  useEffect(() => {
    if (phase !== "revealing") return;

    if (flipIndex >= cards.length) {
      setPhase("guessing");
      return;
    }

    // The first card slides in right away once Begin is pressed - only
    // the gaps between later cards use the slider's chosen speed.
    const delay = flipIndex === 0 ? 0 : transitionDelayMs;
    const timer = setTimeout(() => {
      setFlipIndex((i) => i + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [phase, flipIndex, cards.length, transitionDelayMs]);

  const resetRound = (nextCards) => {
    setCards(nextCards);
    setFlipIndex(0);
    setGuess("");
    setResult(null);
    setPhase("ready");
  };

  const handleGuessSubmit = (e) => {
    e.preventDefault();
    if (guess === "") return;

    const numericGuess = Number(guess);
    const actual = calculateGameCount(cards);
    const correct = numericGuess === actual;
    setResult({ correct, actual, guess: numericGuess });
    setPhase("result");

    // Only ever record history for whoever was actually logged in when
    // the round ended - guests can still play, just without tracking.
    // Recording is best-effort and must never disrupt gameplay if it fails.
    if (user) {
      axios
        .post("/api/v1/game-history", {
          gameName: GAME_NAME,
          result: correct ? "win" : "loss",
        })
        .catch(() => {});
    }
  };

  const handleRetry = () => resetRound(cards);
  const handlePlayAgain = () => resetRound(drawRandomCards(CARD_COUNT));

  const currentCard = flipIndex > 0 ? cards[flipIndex - 1] : null;

  // The slider reads left-to-right as slow-to-fast, which is the inverse
  // of the per-card delay it controls (a bigger delay is slower).
  const speedSliderValue = MIN_DELAY_MS + MAX_DELAY_MS - transitionDelayMs;
  const handleSpeedChange = (val) =>
    setTransitionDelayMs(MIN_DELAY_MS + MAX_DELAY_MS - val);

  return (
    <Flex direction="column" align="center" gap="24px" p={{ base: 4, md: 8 }}>
      <Heading as="h2" size="lg" color="headline.400" fontStyle="italic">
        Practice Counting Game
      </Heading>

      <CardRevealSlot card={currentCard} />

      {phase === "ready" && (
        <Flex
          direction="column"
          align="center"
          gap="20px"
          w="100%"
          maxW="360px"
        >
          <Box w="100%">
            <Text
              mb={2}
              fontWeight="bold"
              color="headline.400"
              textAlign="center"
            >
              Card Transition Speed
            </Text>
            <Flex align="center" gap={3}>
              <Text fontSize="sm" color="gray.600">
                Slow
              </Text>
              <Slider
                flex="1"
                value={speedSliderValue}
                min={MIN_DELAY_MS}
                max={MAX_DELAY_MS}
                step={SPEED_STEP_MS}
                onChange={handleSpeedChange}
              >
                <SliderTrack>
                  <SliderFilledTrack bg="headline.400" />
                </SliderTrack>
                <SliderThumb boxShadow="0 1px 4px rgba(0, 0, 0, 0.3)" />
              </Slider>
              <Text fontSize="sm" color="gray.600">
                Fast
              </Text>
            </Flex>
            <Text mt={1} fontSize="xs" color="gray.500" textAlign="center">
              {(transitionDelayMs / 1000).toFixed(1)}s per card
            </Text>
          </Box>
          <Button
            onClick={() => setPhase("revealing")}
            bg="headline.400"
            color="white"
            _hover={{ bg: "#4a919c" }}
            w="100%"
          >
            Start Game
          </Button>
        </Flex>
      )}

      {phase === "guessing" && (
        <Box as="form" onSubmit={handleGuessSubmit} w="100%" maxW="320px">
          <Text
            mb={2}
            fontWeight="bold"
            color="headline.400"
            textAlign="center"
          >
            What do you think the game count is?
          </Text>
          <NumberInput value={guess} onChange={(val) => setGuess(val)} min={0}>
            <NumberInputField />
          </NumberInput>
          <Button
            mt={3}
            w="100%"
            type="submit"
            bg="headline.400"
            color="white"
            _hover={{ bg: "#4a919c" }}
          >
            Submit Guess
          </Button>
        </Box>
      )}

      {phase === "result" && result && (
        <Flex
          direction="column"
          align="center"
          gap="16px"
          w="100%"
          maxW="400px"
        >
          <Alert
            status={result.correct ? "success" : "error"}
            borderRadius="8px"
          >
            <AlertIcon />
            {result.correct
              ? "Congratulations! You guessed correctly."
              : `Not quite — the correct count was ${result.actual}. You guessed ${result.guess}.`}
          </Alert>
          <Flex gap={3} wrap="wrap" justify="center">
            <Button
              onClick={handleRetry}
              bg="headline.400"
              color="white"
              _hover={{ bg: "#4a919c" }}
            >
              Retry
            </Button>
            <Button
              onClick={handlePlayAgain}
              variant="outline"
              borderColor="headline.400"
              color="headline.400"
            >
              Play Again
            </Button>
            <Button
              onClick={() => navigate("/practice")}
              variant="ghost"
              color="headline.400"
            >
              Back to Practice Games
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
export default PracticeCountingGame;
